import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import { useGetExclusiveProductsAllQuery } from "../store/api.ts";
import {
  addBundleToCart,
  buildCartBundleInstanceId,
} from "../store/cartSlice.js";

import { logAddToCart } from "../utils/analytics.js";

import {
  normalizeExclusiveProducts,
  normalizeStockQuantity,
  hasAvailableExclusiveVariant,
  buildExclusiveCartProduct,
} from "../utils/exclusiveBundle.js";

const defaultCopy = {
  bundleInfoUnavailable: "Bundle information is unavailable.",
  bundleProductsUnavailable: "Some products in this bundle are unavailable.",
  noProductsToAdd: "There are no available products to add.",
  selectEveryBundleSize:
    "Please select a size for every product in the bundle.",
  bundleAddFailed: "One or more bundle products could not be added.",
  bundleProductUnavailable: (name) =>
    `${name || "A bundle product"} is no longer available in the selected size.`,
  bundleAdded: (name) => `${name} added to cart`,
  bundleInfoCurrentlyUnavailable:
    "Bundle information is currently unavailable.",
  bundleHasNoProducts: "This bundle does not contain any products.",
  bundleProductsCurrentlyUnavailable:
    "Some products in this bundle are currently unavailable.",
  bundleProductSoldOut: (name) =>
    `${name || "A bundle product"} is currently sold out.`,
};

export const useExclusiveBundleFlow = ({
  enabled = true,
  copy = defaultCopy,
  onBundleAdded,
} = {}) => {
  const dispatch = useDispatch();

  const [isSizingModalOpen, setIsSizingModalOpen] = useState(false);
  const [sizingModalMode, setSizingModalMode] = useState("cart");

  const {
    data: exclusiveProductsResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetExclusiveProductsAllQuery(
    {
      page: 1,
      pageSize: 20,
    },
    {
      skip: !enabled,
    },
  );

  const collectionPieces = useMemo(
    () => normalizeExclusiveProducts(exclusiveProductsResponse),
    [exclusiveProductsResponse],
  );

  const bundleDetails = useMemo(() => {
    const bundle = exclusiveProductsResponse?.bundle;

    if (!bundle?.id) return null;

    const productIds = Array.isArray(bundle?.product_ids)
      ? [...new Set(bundle.product_ids.map(Number).filter(Number.isFinite))]
      : [];

    return {
      id: String(bundle.id),
      name: String(bundle.name || "Collection Bundle"),
      slug: String(bundle.slug || ""),
      description: String(bundle.description || ""),
      discountPercent: Math.min(
        100,
        Math.max(0, Number(bundle.discount_percent || 0)),
      ),
      imageUrl: String(bundle.image_url || ""),
      productIds,
    };
  }, [exclusiveProductsResponse]);

  const bundleProducts = useMemo(() => {
    if (!bundleDetails?.productIds?.length) return [];

    const ids = new Set(bundleDetails.productIds.map(String));

    return collectionPieces.filter((product) => ids.has(String(product.id)));
  }, [bundleDetails, collectionPieces]);

  const hasCompleteBundle =
    Boolean(bundleDetails?.id) &&
    bundleDetails.productIds.length > 0 &&
    bundleProducts.length === bundleDetails.productIds.length;

  const openBundleModal = useCallback(
    (mode = "cart") => {
      if (!bundleDetails) {
        toast.error(copy.bundleInfoCurrentlyUnavailable);
        return;
      }

      if (!bundleDetails.productIds.length) {
        toast.error(copy.bundleHasNoProducts);
        return;
      }

      if (!hasCompleteBundle) {
        toast.error(copy.bundleProductsCurrentlyUnavailable);
        return;
      }

      const unavailableProduct = bundleProducts.find(
        (product) => !hasAvailableExclusiveVariant(product),
      );

      if (unavailableProduct) {
        toast.error(copy.bundleProductSoldOut(unavailableProduct.name));
        return;
      }

      setSizingModalMode(mode);
      setIsSizingModalOpen(true);
    },
    [bundleDetails, bundleProducts, hasCompleteBundle, copy],
  );

  const confirmBundle = useCallback(
    (selections) => {
      if (!bundleDetails?.id) {
        toast.error(copy.bundleInfoUnavailable);
        return;
      }

      if (!hasCompleteBundle) {
        toast.error(copy.bundleProductsUnavailable);
        return;
      }

      if (!Array.isArray(selections) || !selections.length) {
        toast.error(copy.noProductsToAdd);
        return;
      }

      const expectedIds = new Set(bundleDetails.productIds.map(String));

      const selectedIds = new Set(
        selections
          .map(({ product }) => product?.id)
          .filter((id) => id !== null && id !== undefined)
          .map(String),
      );

      const hasEveryProduct =
        expectedIds.size === selectedIds.size &&
        [...expectedIds].every((id) => selectedIds.has(id));

      if (!hasEveryProduct) {
        toast.error(copy.selectEveryBundleSize);
        return;
      }

      const bundleItems = [];

      for (const { product, variant } of selections) {
        if (!product || !variant || !expectedIds.has(String(product.id))) {
          toast.error(copy.bundleAddFailed);
          return;
        }

        const quantity = normalizeStockQuantity(variant?.quantity);

        if (
          variant?.isOutOfStock === true ||
          (quantity !== null && quantity <= 0)
        ) {
          toast.error(copy.bundleProductUnavailable(product.name));
          return;
        }

        const cartProduct = buildExclusiveCartProduct(product, variant);

        if (!cartProduct) {
          toast.error(copy.bundleAddFailed);
          return;
        }

        try {
          logAddToCart({
            id: cartProduct.id,
            name: cartProduct.name,
            price: cartProduct.price,
            category: cartProduct.garmentType || "Fashion",
            variant: cartProduct.variant_id,
            size: cartProduct.size,
            quantity: 1,
          });
        } catch (error) {
          console.log("Analytics add to cart logging failed", error);
        }

        bundleItems.push({
          product: cartProduct,
          quantity: 1,
        });
      }

      const bundleInstanceId = buildCartBundleInstanceId(
        bundleDetails,
        bundleItems,
      );

      dispatch(
        addBundleToCart({
          bundle: {
            id: bundleDetails.id,
            name: bundleDetails.name,
            discountPercent: bundleDetails.discountPercent,
          },
          items: bundleItems,
        }),
      );

      setIsSizingModalOpen(false);

      onBundleAdded?.({
        bundleId: bundleDetails.id,
        bundleInstanceId,
        bundle: bundleDetails,
        items: bundleItems,
      });

      toast.success(copy.bundleAdded(bundleDetails.name));
    },
    [bundleDetails, hasCompleteBundle, dispatch, copy, onBundleAdded],
  );

  return {
    collectionPieces,
    bundleDetails,
    bundleProducts,

    isLoading,
    isFetching,
    isError,
    isBundleLoading: isLoading || isFetching,

    isSizingModalOpen,
    sizingModalMode,

    openBundleModal,
    closeBundleModal: () => setIsSizingModalOpen(false),
    confirmBundle,
  };
};
